/**
 * Admin → Flow → Session Access → "Type 2 customers — own Gmail" (2026-10-04).
 *
 * Why it exists: "Assign customers" above covers Type 1 only (many customers share one
 * Gmail). The admin assumed it covered every Flow product, so Type 2 gets its own list on
 * the same page: which Gmail each customer holds, credits used, when access ends, and
 * whether that Gmail's password is actually stored in the vault.
 *
 * 2026-10-18 — "Count credits" by robot (PLAN_FLOW_T2_KIRA_CREDIT.md): Type 2 credit is only
 * counted when the CUSTOMER opens Flow. Phone customers never do, so the admin presses one
 * button and a robot on the admin PC opens each GenLogin profile, reads the Flow activity
 * page, and the server counts it (same engine as the extension). This list gains: a
 * "Count credits now" button, a GenLogin profile ID per customer, and a per-customer robot
 * status (green = counted, red = needs login / error) with a Retry button.
 *
 * The access + robot records (flowT2_akses, flowT2_robot, flowT2_kira) are server-only, so
 * everything comes from the Flow server (services/flowT2Admin.js). No password ever reaches
 * this page. Styles: reuses the fsp-* classes of FlowSessionPilotTab.css.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { muatSenaraiJenis2, semakVaultJenis2, mulaKiraJenis2, tetapProfilJenis2 } from '../services/flowT2Admin';
import { loadFlowSubscriptions } from '../services/flowSessionCustomers';
import { terasNombor } from '../utils/nomborTelefon';

const dateShort = value => value ? new Date(value).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const timeShort = value => value ? new Date(value).toLocaleString('en-MY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
const num = value => Number(value || 0).toLocaleString('en-MY');
const profilSah = p => /^\d{1,15}$/.test(String(p || '').trim());

const VAULT = {
    saved: { tone: 'ok', label: 'Saved' },
    missing: { tone: 'bad', label: 'Not saved' },
    mismatch: { tone: 'bad', label: 'Vault holds a different Gmail' },
    incomplete: { tone: 'bad', label: 'No password in vault' },
    unknown: { tone: 'warn', label: 'Could not check' },
};

// Robot result per customer. giliran/baca = in progress; ok = counted; perlu_login/ralat = red.
const ROBOT = {
    ok: { tone: 'ok', label: 'Counted' },
    giliran: { tone: 'idle', label: 'Queued…' },
    baca: { tone: 'idle', label: 'Reading…' },
    perlu_login: { tone: 'bad', label: 'Needs login' },
    ralat: { tone: 'bad', label: 'Error' },
};

const susun = baris => [...baris].sort((a, b) => (a.aktif === b.aktif
    ? (a.aktif ? (a.endAt || 0) - (b.endAt || 0) : (b.endAt || 0) - (a.endAt || 0))
    : (a.aktif ? -1 : 1)));

export default function FlowT2Senarai({ muatSemula = 0 }) {
    const [data, setData] = useState(null), [error, setError] = useState(''), [loading, setLoading] = useState(false);
    const [phones, setPhones] = useState({});
    const [cari, setCari] = useState('');
    const [vault, setVault] = useState(null);
    const [busy, setBusy] = useState('');            // '' | 'all' | 'retry:<uid>' | 'profil:<uid>'
    const [aksiMsg, setAksiMsg] = useState(''), [aksiErr, setAksiErr] = useState('');
    const giliran = useRef(0);

    // senyap = poll refresh (no spinner, no vault re-check — the vault call is ~27 s).
    const muat = useCallback(async (senyap = false) => {
        const saya = ++giliran.current;
        const terkini = () => giliran.current === saya;
        if (!senyap) { setLoading(true); setError(''); setVault(null); }
        try {
            const d = await muatSenaraiJenis2();
            if (!terkini()) return;
            setData(d);
            loadFlowSubscriptions(d.baris.map(b => b.uid)).then(m => { if (terkini()) setPhones(m); }).catch(() => { if (terkini()) setPhones({}); });
            if (!senyap && d.baris.some(b => b.aktif)) {
                setVault({ state: 'checking' });
                semakVaultJenis2().then(v => { if (terkini()) setVault({ state: 'done', semak: v.semak }); })
                    .catch(e => { if (terkini()) setVault({ state: 'failed', message: e.message || 'Vault check failed.' }); });
            }
        } catch (e) {
            if (terkini() && !senyap) { setError(e.message || 'The Type 2 list could not be loaded.'); setData(null); }
        } finally {
            if (terkini() && !senyap) setLoading(false);
        }
    }, []);
    useEffect(() => { muat(); }, [muat, muatSemula]);

    // While the robot run is active, poll silently so progress + red statuses update on screen.
    const jobBerjalan = data?.job?.status === 'running';
    useEffect(() => {
        if (!jobBerjalan) return;
        const t = setInterval(() => muat(true), 5000);
        return () => clearInterval(t);
    }, [jobBerjalan, muat]);

    const baris = data ? susun(data.baris) : [];
    // Search (2026-10-20): email / Gmail / WhatsApp. Phone compares digits, 0…/60…/+60… equal.
    const padanCari = b => {
        const t = cari.toLowerCase().trim();
        if (!t) return true;
        const phone = phones[b.uid]?.phone || '';
        if ([b.sesaatEmail, b.gmail, b.uid, phone].some(f => String(f ?? '').toLowerCase().includes(t))) return true;
        const digit = t.replace(/\D/g, ''), phoneD = String(phone).replace(/\D/g, '');
        if (digit.length >= 2 && phoneD) {
            if (phoneD.includes(digit)) return true;
            const teras = terasNombor(digit);
            if (teras && teras !== digit && terasNombor(phoneD).startsWith(teras)) return true;
        }
        return false;
    };
    const paparan = baris.filter(padanCari);
    const robot = data?.robot || {};
    const job = data?.job || null;
    const berganda = [...new Set(baris.filter(b => b.berganda).map(b => b.gmail))];
    const bolehKira = baris.some(b => b.aktif && profilSah(robot[b.uid]?.profileId));
    // Progress: customers whose robot result landed after this run started.
    const siapKira = job && job.status === 'running'
        ? baris.filter(b => { const r = robot[b.uid]; return r && r.pada && job.mula && r.pada >= job.mula && ['ok', 'perlu_login', 'ralat'].includes(r.status); }).length : 0;

    const kiraSemua = async () => {
        setBusy('all'); setAksiErr(''); setAksiMsg('');
        try {
            const r = await mulaKiraJenis2({ scope: 'all' });
            setAksiMsg(`Robot started — ${r.total} Gmail${r.total === 1 ? '' : 's'} queued.${r.tiadaProfil ? ` ${r.tiadaProfil} customer${r.tiadaProfil === 1 ? '' : 's'} skipped (no profile ID set).` : ''} Keep the robot running on your PC.`);
            await muat(true);
        } catch (e) { setAksiErr(e.message || 'Could not start the robot.'); }
        finally { setBusy(''); }
    };
    const retry = async uid => {
        setBusy('retry:' + uid); setAksiErr(''); setAksiMsg('');
        try { await mulaKiraJenis2({ scope: 'satu', uid }); await muat(true); }
        catch (e) { setAksiErr(e.message || 'Could not retry.'); }
        finally { setBusy(''); }
    };
    const simpanProfil = async (uid, nilai) => {
        const v = String(nilai || '').trim(), cur = robot[uid]?.profileId || '';
        if (v === cur) return;
        if (v && !profilSah(v)) { setAksiErr('GenLogin profile ID must be digits only.'); return; }
        setBusy('profil:' + uid); setAksiErr(''); setAksiMsg('');
        try { await tetapProfilJenis2({ uid, profileId: v }); await muat(true); }
        catch (e) { setAksiErr(e.message || 'Could not save the profile ID.'); }
        finally { setBusy(''); }
    };

    const kreditCell = b => {
        const penuh = b.had > 0 && b.dahGuna >= b.had;
        return <span className="fsp-status">{penuh && <span className="fsp-dot fsp-tone-bad" aria-hidden="true" />}{num(b.dahGuna)} / {b.had > 0 ? num(b.had) : 'no limit'}
            <small>{penuh ? ' · limit reached' : !b.kreditPada ? ' · not read yet' : ` · read ${timeShort(b.kreditPada)}`}</small></span>;
    };
    const aksesCell = b => {
        const tamat = b.endAt && b.endAt <= Date.now();
        const st = b.aktif ? { tone: 'ok', label: 'Active' } : !b.enabled ? { tone: 'idle', label: 'Disabled' } : tamat ? { tone: 'idle', label: 'Expired' } : { tone: 'idle', label: 'Not active' };
        return <span className="fsp-status">{dateShort(b.endAt)} <small>· <span className={`fsp-dot fsp-tone-${st.tone}`} aria-hidden="true" />{st.label}</small></span>;
    };
    const profilCell = b => <input type="text" inputMode="numeric" defaultValue={robot[b.uid]?.profileId || ''}
        placeholder="GenLogin ID" aria-label={`GenLogin profile ID for ${b.sesaatEmail || b.gmail}`} style={{ width: '96px' }}
        disabled={busy === 'profil:' + b.uid}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        onBlur={e => simpanProfil(b.uid, e.currentTarget.value)} />;
    const robotCell = b => {
        const r = robot[b.uid] || {};
        if (!profilSah(r.profileId)) return <span className="fsp-muted">Set a profile ID first</span>;
        const retryBtn = <button type="button" className="fsp-refresh" disabled={!!busy} onClick={() => retry(b.uid)}>{busy === 'retry:' + b.uid ? 'Starting…' : 'Retry'}</button>;
        const st = ROBOT[r.status];
        if (!st) return <span className="fsp-status"><span className="fsp-muted">Not counted yet</span> {retryBtn}</span>;
        const merah = r.status === 'perlu_login' || r.status === 'ralat';
        return <span className="fsp-status"><span className={`fsp-dot fsp-tone-${st.tone}`} aria-hidden="true" />{st.label}
            {r.pada ? <small> · {timeShort(r.pada)}</small> : null}
            {r.status === 'ralat' && r.reason ? <small title={r.reason}> · {r.reason.slice(0, 40)}</small> : null}
            {merah ? <> {retryBtn}</> : null}</span>;
    };
    const vaultCell = b => {
        if (!b.aktif) return '—';
        if (!vault || vault.state === 'checking') return <span className="fsp-muted">Checking…</span>;
        const v = vault.state === 'done' ? vault.semak[b.uid] : null;
        const st = VAULT[v?.status] || VAULT.unknown;
        const hint = v?.status === 'saved' ? (v.ada2fa ? ' · with 2FA key' : '')
            : ['missing', 'mismatch', 'incomplete'].includes(v?.status) && b.vaultId ? ` · store it as gmail-${b.vaultId}` : '';
        return <span className="fsp-status"><span className={`fsp-dot fsp-tone-${st.tone}`} aria-hidden="true" />{st.label}{hint && <small>{hint}</small>}</span>;
    };

    return <section className="fsp-card"><h3>Type 2 customers — own Gmail</h3>
        <p className="fsp-muted">One Gmail for one customer, set in Subscriptions. Set each customer's GenLogin profile ID, then press “Count credits now” — a robot on your PC opens each profile, reads the Flow activity page, and fills in the credits used here. The robot only reads; log each Gmail in manually once.</p>
        <div className="fsp-actions">
            <button type="button" className="fsp-primary" disabled={!!busy || !bolehKira} onClick={kiraSemua}>
                {busy === 'all' ? 'Starting…' : 'Count credits now'}
            </button>
            {!bolehKira && !loading && <span className="fsp-muted">Set a GenLogin profile ID for at least one active customer first.</span>}
        </div>
        {job?.status === 'running' && <p role="status" className="fsp-notice">Robot running… {siapKira} / {job.total} Gmail{job.total === 1 ? '' : 's'} done. Leave the robot running on your PC; this updates on its own.</p>}
        {job?.status === 'done' && job.siap && <p className="fsp-muted">Last run finished {timeShort(job.siap)}.</p>}
        {aksiMsg && <p role="status" className="fsp-notice">{aksiMsg}</p>}
        {aksiErr && <p role="alert" className="fsp-error">{aksiErr}</p>}
        {loading && <p role="status" className="fsp-muted">Loading…</p>}
        {error && <p role="alert" className="fsp-error">{error}</p>}
        {data && <>
            {data.terpotong && <p role="alert" className="fsp-error">Only the first 500 records are shown, so this list is incomplete. Contact the developer.</p>}
            {!!data.rosak && <p role="alert" className="fsp-error">{data.rosak} record{data.rosak === 1 ? ' is' : 's are'} malformed and not shown. Contact the developer.</p>}
            {!!berganda.length && <p role="alert" className="fsp-error">The same Gmail is given to more than one customer: {berganda.join(', ')}. Give one of them a different Gmail (Change to Type 2), or end one access.</p>}
            {vault?.state === 'failed' && <p role="alert" className="fsp-error">The vault could not be checked: {vault.message} Press Refresh at the top of this page to try again.</p>}
            <div className="fsp-legend"><span><span className="fsp-dot fsp-tone-ok" />Counted / saved</span><span><span className="fsp-dot fsp-tone-bad" />Needs login / not saved</span><span><span className="fsp-dot fsp-tone-warn" />Could not check</span></div>
            {!!baris.length && <div className="fsp-filter">
                <label className="fsp-search">Search Type 2 customers<input type="search" value={cari} maxLength={80} onChange={e => setCari(e.target.value)} placeholder="Email, Gmail, WhatsApp…" /></label>
                <span className="fsp-muted" role="status">{cari.trim() ? `Showing ${paparan.length} of ${baris.length}` : `${baris.length} customer${baris.length === 1 ? '' : 's'}`}</span>
            </div>}
            <div className="fsp-table"><table><thead><tr><th>Customer</th><th>WhatsApp</th><th>Gmail given</th><th>GenLogin ID</th><th>Started</th><th>Credits used</th><th>Access until</th><th>Robot</th><th>Password in vault</th></tr></thead>
                <tbody>{!baris.length && <tr><td colSpan={9}>No Type 2 customers yet.</td></tr>}
                    {!!baris.length && !paparan.length && <tr><td colSpan={9}>No Type 2 customer matches “{cari.trim()}”. <button type="button" onClick={() => setCari('')}>Clear search</button></td></tr>}
                    {paparan.map(b => <tr key={b.uid}>
                        <td>{b.sesaatEmail || b.uid}</td>
                        <td>{phones[b.uid]?.phone || '—'}</td>
                        <td>{b.gmail}</td>
                        <td>{profilCell(b)}</td>
                        <td>{dateShort(b.kiraDari)}</td>
                        <td>{kreditCell(b)}</td>
                        <td>{aksesCell(b)}</td>
                        <td>{robotCell(b)}</td>
                        <td>{vaultCell(b)}</td></tr>)}</tbody></table></div>
        </>}
    </section>;
}
